import React, { useState, useEffect } from "react";
import {
  Popover,
  Button,
  Box,
  Typography,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { IRoleMinDTO, IDepartmentMinDTO } from "../../interfaces/UserManagement";
import { getRolesMin, getDeptsMin, searchFilterUsers } from "../../services/UserManagement";

interface UsersFilterDialogProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApply: (filters: { roles: number[]; departments: number[]; statuses: string[] }, data: any[]) => void;
  search: string;
}

const UsersFilterDialog = ({ open, anchorEl, onClose, onApply, search }: UsersFilterDialogProps) => {
  const [activeFilter, setActiveFilter] = useState("Role");
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [roleOptions, setRoleOptions] = useState<IRoleMinDTO[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<IDepartmentMinDTO[]>([]);
  const [applying, setApplying] = useState(false);

  const statusOptions = ["Active", "Inactive"];

  useEffect(() => {
    if (!open) return;
    (async () => {
      const rolesData = await getRolesMin();
      if (rolesData) setRoleOptions(rolesData);
      const deptsData = await getDeptsMin();
      if (deptsData) setDepartmentOptions(deptsData);
    })();
  }, [open]);

  const handleToggle = (value: number | string, type: string) => {
    if (type === "Role") {
      setSelectedRoles((prev) => prev.includes(value as number) ? prev.filter((s) => s !== value) : [...prev, value as number]);
    } else if (type === "Department") {
      setSelectedDepartments((prev) => prev.includes(value as number) ? prev.filter((s) => s !== value) : [...prev, value as number]);
    } else if (type === "Status") {
      setSelectedStatuses((prev) => prev.includes(value as string) ? prev.filter((s) => s !== value) : [...prev, value as string]);
    }
  };

  const handleSelectAll = () => {
    if (activeFilter === "Role") {
      setSelectedRoles(selectedRoles.length === roleOptions.length ? [] : roleOptions.map((r) => r.id));
    } else if (activeFilter === "Department") {
      setSelectedDepartments(selectedDepartments.length === departmentOptions.length ? [] : departmentOptions.map((d) => d.id));
    } else if (activeFilter === "Status") {
      setSelectedStatuses(selectedStatuses.length === statusOptions.length ? [] : statusOptions);
    }
  };

  const handleClearAll = () => {
    setSelectedRoles([]);
    setSelectedDepartments([]);
    setSelectedStatuses([]);
  };

  const handleApply = async () => {
    try {
      setApplying(true);
      const res = await searchFilterUsers(selectedStatuses, selectedRoles, selectedDepartments, search);
      onApply({ roles: selectedRoles, departments: selectedDepartments, statuses: selectedStatuses }, Array.isArray(res) ? res : []);
    } catch {
      onApply({ roles: selectedRoles, departments: selectedDepartments, statuses: selectedStatuses }, []);
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    setSelectedRoles([]);
    setSelectedDepartments([]);
    setSelectedStatuses([]);
    onClose();
  };

  const renderRightContent = () => {
    if (activeFilter === "Status") {
      return (
        <Box sx={{ border: "1px solid", borderColor: "grey.300", borderRadius: "6px", p: 1.5 }}>
          <FormControlLabel
            control={<Checkbox checked={selectedStatuses.length === statusOptions.length} indeterminate={selectedStatuses.length > 0 && selectedStatuses.length < statusOptions.length} onChange={handleSelectAll} sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }} />}
            label={<Typography variant="body2">Select All</Typography>}
          />
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ maxHeight: "150px", overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
            <FormGroup>
              {statusOptions.map((s) => (
                <FormControlLabel key={s} control={<Checkbox checked={selectedStatuses.includes(s)} onChange={() => handleToggle(s, "Status")} sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" } }} />} label={<Typography variant="body2">{s}</Typography>} />
              ))}
            </FormGroup>
          </Box>
        </Box>
      );
    }

    const isRole = activeFilter === "Role";
    const options = isRole ? roleOptions : departmentOptions;
    const selected = isRole ? selectedRoles : selectedDepartments;

    return (
      <Box sx={{ border: "1px solid", borderColor: "grey.300", borderRadius: "6px", p: 1.5 }}>
        <FormControlLabel
          control={<Checkbox checked={selected.length === options.length} indeterminate={selected.length > 0 && selected.length < options.length} onChange={handleSelectAll} sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }} />}
          label={<Typography variant="body2">Select All</Typography>}
        />
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ maxHeight: "150px", overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
          <FormGroup>
            {options.map((opt) => (
              <FormControlLabel key={opt.id} control={<Checkbox checked={selected.includes(opt.id)} onChange={() => handleToggle(opt.id, activeFilter)} sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" } }} />} label={<Typography variant="body2">{isRole ? (opt as IRoleMinDTO).roleName : (opt as IDepartmentMinDTO).departmentName}</Typography>} />
            ))}
          </FormGroup>
        </Box>
      </Box>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { borderRadius: "0.75rem", width: "450px", mt: 1 } }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>Filter</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      
      <Divider />

      <Box sx={{ p: 0, display: "flex", height: "250px", backgroundColor: "background.default" }}>
        <Box sx={{ width: "40%", borderRight: "1px solid", borderColor: "grey.300", p: 1.5, backgroundColor: "background.default" }}>
          {["Role", "Department", "Status"].map((filter) => (
            <Box
              key={filter}
              onClick={() => setActiveFilter(filter)}
              sx={{
                p: 1,
                cursor: "pointer",
                borderRadius: "6px",
                mb: 0.5,
                backgroundColor: activeFilter === filter ? "white" : "transparent",
                color: activeFilter === filter ? "primary.main" : "text.primary",
                fontWeight: activeFilter === filter ? 600 : 400,
              }}
            >
              <Typography variant="body2">{filter}</Typography>
            </Box>
          ))}
        </Box>
        
        <Box sx={{ width: "60%", p: 1.5, backgroundColor: "white", overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
          {renderRightContent()}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", backgroundColor: "background.default" }}>
        <Button onClick={handleClearAll} sx={{ textTransform: "none", color: "text.primary", "&:hover": { backgroundColor: "transparent" } }}>
          Clear all
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={handleClose} sx={{ borderRadius: "6px", textTransform: "none", borderColor: "grey.300", color: "text.primary", backgroundColor: "#fff", "&:hover": { backgroundColor: "#fff", borderColor: "grey.300" } }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply} disabled={applying} sx={{ borderRadius: "6px", textTransform: "none", backgroundColor: "primary.main", "&:hover": { backgroundColor: "primary.main" } }}>
            {applying ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "OK"}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
export default UsersFilterDialog;
